<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCrmStore } from 'dashboard/stores/crm';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import crmApi from 'dashboard/api/crm';
import contactsAPI from 'dashboard/api/contacts';

const props = defineProps({
  stageId: { type: Number, default: null },
  contactId: { type: Number, default: null },
  opportunityId: { type: Number, default: null },
  conversationId: { type: Number, default: null },
});

const emit = defineEmits(['close']);

const dialogRef = ref(null);

const { t } = useI18n();
const crmStore = useCrmStore();
const store = useStore();
const getters = useStoreGetters();

const allAgents = computed(() => getters['agents/getAgents'].value ?? []);

// Form fields
const title = ref('');
const valueCents = ref('');
const selectedStageId = ref(props.stageId);
const localFunnelId = ref(crmStore.currentFunnelId);
const localStages = ref([]);
const localFunnels = ref([]);
const isLoadingMeta = ref(false);
const selectedAgentId = ref(null);
const status = ref('open');
const crmOriginId = ref(null);
const originOptions = ref([]);
const probability = ref('');
const closeDate = ref('');
const nextContactAt = ref('');
const productService = ref('');
const notes = ref('');
const company = ref('');
const lostReason = ref('');
const lostReasonError = ref(false);

// Contact search
const contactQuery = ref('');
const contactResults = ref([]);
const selectedContact = ref(null);
const showContactDropdown = ref(false);
const contactSearchLoading = ref(false);

const isEditMode = computed(() => !!props.opportunityId);
const isLoading = computed(() => crmStore.isLoading);

const stages = computed(() => localStages.value);

const funnels = computed(() =>
  crmStore.funnels.length > 0 ? crmStore.funnels : localFunnels.value
);

const activeFunnelId = computed(
  () => localFunnelId.value || crmStore.currentFunnelId
);

const loadFunnelsAndStages = async () => {
  if (crmStore.funnels.length === 0) {
    isLoadingMeta.value = true;
    try {
      await crmStore.fetchFunnels();
    } finally {
      isLoadingMeta.value = false;
    }
  }
  if (!localFunnelId.value) localFunnelId.value = crmStore.currentFunnelId;
  if (localFunnelId.value) {
    const { data } = await crmApi.getStages(localFunnelId.value);
    localStages.value = data.payload || data;
    if (!selectedStageId.value && localStages.value.length > 0) {
      selectedStageId.value = localStages.value[0].id;
    }
  }
};

watch(localFunnelId, async newId => {
  if (!newId) return;
  const { data } = await crmApi.getStages(newId);
  localStages.value = data.payload || data;
  selectedStageId.value = localStages.value[0]?.id || null;
});

// Contact search
let searchTimeout = null;
const searchContacts = query => {
  clearTimeout(searchTimeout);
  if (!query.trim()) {
    contactResults.value = [];
    showContactDropdown.value = false;
    return;
  }
  searchTimeout = setTimeout(async () => {
    contactSearchLoading.value = true;
    try {
      const { data } = await contactsAPI.search(query, 1, 'name');
      contactResults.value = data.payload || [];
      showContactDropdown.value = true;
    } finally {
      contactSearchLoading.value = false;
    }
  }, 300);
};

const selectContact = contact => {
  selectedContact.value = contact;
  contactQuery.value = contact.name;
  showContactDropdown.value = false;
  if (contact.company_name) company.value = contact.company_name;
};

const clearContact = () => {
  selectedContact.value = null;
  contactQuery.value = '';
  contactResults.value = [];
};

const prefillFromOpportunity = opp => {
  title.value = opp.title || '';
  valueCents.value =
    opp.value_cents != null ? (opp.value_cents / 100).toFixed(2) : '';
  selectedStageId.value = opp.funnel_stage_id || null;
  localFunnelId.value = opp.funnel_id || crmStore.currentFunnelId;
  selectedAgentId.value = opp.assigned_agent_id || null;
  status.value = opp.status || 'open';
  crmOriginId.value = opp.crm_origin_id ?? null;
  probability.value = opp.probability != null ? String(opp.probability) : '';
  closeDate.value = opp.close_date ? opp.close_date.slice(0, 10) : '';
  nextContactAt.value = opp.next_contact_at
    ? opp.next_contact_at.slice(0, 16)
    : '';
  productService.value = opp.product_service || '';
  notes.value = opp.notes || '';
  company.value = opp.company || '';
  lostReason.value = opp.lost_reason || '';
  if (opp.contact) {
    selectedContact.value = opp.contact;
    contactQuery.value = opp.contact.name;
  }
};

onMounted(async () => {
  dialogRef.value?.open();
  store.dispatch('agents/get');
  crmApi.getOrigins().then(({ data }) => {
    originOptions.value = (data.payload || data).filter(o => o.active);
  });
  await loadFunnelsAndStages();
  if (props.opportunityId) {
    const existing = crmStore.opportunities.find(
      o => o.id === props.opportunityId
    );
    if (existing) {
      prefillFromOpportunity(existing);
    } else {
      const { data } = await crmApi.getOpportunity(props.opportunityId);
      prefillFromOpportunity(data.payload || data);
    }
  }
});

const handleConfirm = async () => {
  if (!title.value.trim()) return;
  lostReasonError.value = status.value === 'lost' && !lostReason.value.trim();
  if (lostReasonError.value) return;

  const data = {
    title: title.value.trim(),
    funnel_id: activeFunnelId.value,
    funnel_stage_id: selectedStageId.value || stages.value[0]?.id,
    status: status.value,
    crm_origin_id: crmOriginId.value,
  };
  if (valueCents.value !== '')
    data.value_cents = Math.round(Number(valueCents.value) * 100);
  if (selectedAgentId.value) data.assigned_agent_id = selectedAgentId.value;
  if (probability.value !== '') data.probability = Number(probability.value);
  if (closeDate.value) data.close_date = closeDate.value;
  if (nextContactAt.value) data.next_contact_at = nextContactAt.value;
  if (productService.value.trim())
    data.product_service = productService.value.trim();
  if (notes.value.trim()) data.notes = notes.value.trim();
  if (company.value.trim()) data.company = company.value.trim();
  if (status.value === 'lost') data.lost_reason = lostReason.value.trim();
  if (props.contactId) data.contact_id = props.contactId;
  else if (selectedContact.value) data.contact_id = selectedContact.value.id;
  if (!isEditMode.value && props.conversationId)
    data.conversation_id = props.conversationId;

  const result = isEditMode.value
    ? await crmStore.updateOpportunity(props.opportunityId, data)
    : await crmStore.createOpportunity(data);
  if (result) emit('close');
};

const handleCancel = () => emit('close');

const statusOptions = [
  { value: 'open', label: t('CRM.PANEL.STATUS_OPEN') },
  { value: 'won', label: t('CRM.PANEL.STATUS_WON') },
  { value: 'lost', label: t('CRM.PANEL.STATUS_LOST') },
];
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="isEditMode ? t('CRM.FORM.TITLE_EDIT') : t('CRM.FORM.TITLE')"
    :confirm-button-label="t('CRM.FORM.SAVE')"
    :cancel-button-label="t('CRM.FORM.CANCEL')"
    :is-loading="isLoading"
    width="2xl"
    @confirm="handleConfirm"
    @close="handleCancel"
  >
    <div class="flex flex-col gap-4 max-h-[65vh] overflow-y-auto pr-1">
      <div
        v-if="isLoadingMeta"
        class="text-sm text-n-slate-10 text-center py-2"
      >
        {{ t('CRM.SETTINGS.LOADING') }}
      </div>
      <template v-else>
        <!-- Funnel selector (only if more than 1) -->
        <div v-if="funnels.length > 1" class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-12">{{
            t('CRM.FORM.LABEL_FUNNEL')
          }}</label>
          <select
            v-model="localFunnelId"
            class="border border-n-weak rounded px-2 py-1.5 text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500"
          >
            <option
              v-for="funnel in funnels"
              :key="funnel.id"
              :value="funnel.id"
            >
              {{ funnel.name }}
            </option>
          </select>
        </div>

        <!-- Row 1: Título -->
        <Input
          v-model="title"
          :label="t('CRM.FORM.LABEL_TITLE')"
          :placeholder="t('CRM.FORM.PLACEHOLDER_TITLE')"
        />

        <!-- Row 2: Contato (only if no contactId prop) -->
        <div v-if="!props.contactId" class="flex flex-col gap-1 relative">
          <label class="text-xs font-medium text-n-slate-12">{{
            t('CRM.FORM.LABEL_CONTACT')
          }}</label>
          <div class="flex items-center gap-2">
            <input
              v-model="contactQuery"
              type="text"
              :placeholder="t('CRM.FORM.PLACEHOLDER_CONTACT')"
              class="flex-1 border border-n-weak rounded px-2 py-1.5 text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500"
              @input="searchContacts(contactQuery)"
              @blur="
                setTimeout(() => {
                  showContactDropdown = false;
                }, 200)
              "
            />
            <button
              v-if="selectedContact"
              class="text-n-ruby-11 hover:text-n-ruby-12"
              :title="t('CRM.FORM.CLEAR_CONTACT')"
              @click="clearContact"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div
            v-if="showContactDropdown && contactResults.length > 0"
            class="absolute top-full left-0 right-0 z-50 mt-1 bg-n-surface-1 border border-n-weak rounded shadow-lg max-h-40 overflow-y-auto"
          >
            <button
              v-for="c in contactResults"
              :key="c.id"
              class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-2 transition-colors"
              @click="selectContact(c)"
            >
              {{ c.name }}
              <span v-if="c.email" class="text-xs text-n-slate-10 ml-1">{{
                c.email
              }}</span>
            </button>
          </div>
        </div>

        <!-- Row 3: Empresa + Produto/Serviço -->
        <div class="grid grid-cols-2 gap-3">
          <Input
            v-model="company"
            :label="t('CRM.FORM.LABEL_COMPANY')"
            :placeholder="t('CRM.FORM.PLACEHOLDER_COMPANY')"
          />
          <Input
            v-model="productService"
            :label="t('CRM.FORM.LABEL_PRODUCT')"
            :placeholder="t('CRM.FORM.PLACEHOLDER_PRODUCT')"
          />
        </div>

        <!-- Row 4: Valor + Probabilidade -->
        <div class="grid grid-cols-2 gap-3">
          <Input
            v-model="valueCents"
            type="number"
            :label="t('CRM.FORM.LABEL_VALUE')"
            :placeholder="t('CRM.FORM.PLACEHOLDER_VALUE')"
          />
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-12">{{
              t('CRM.FORM.LABEL_PROBABILITY')
            }}</label>
            <input
              v-model="probability"
              type="number"
              min="0"
              max="100"
              :placeholder="t('CRM.FORM.PLACEHOLDER_PROBABILITY')"
              class="border border-n-weak rounded px-2 py-1.5 text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
          </div>
        </div>

        <!-- Row 5: Estágio + Agente -->
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-12">{{
              t('CRM.FORM.LABEL_STAGE')
            }}</label>
            <p v-if="stages.length === 0" class="text-sm text-n-slate-10">
              {{ t('CRM.FORM.NO_STAGES') }}
            </p>
            <select
              v-else
              v-model="selectedStageId"
              class="border border-n-weak rounded px-2 py-1.5 text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500"
            >
              <option v-for="stage in stages" :key="stage.id" :value="stage.id">
                {{ stage.name }}
              </option>
            </select>
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-12">{{
              t('CRM.FORM.LABEL_AGENT')
            }}</label>
            <select
              v-model="selectedAgentId"
              class="border border-n-weak rounded px-2 py-1.5 text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500"
            >
              <option :value="null">{{ t('CRM.FORM.NO_AGENT') }}</option>
              <option
                v-for="agent in allAgents"
                :key="agent.id"
                :value="agent.id"
              >
                {{ agent.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Row 6: Status + Origem -->
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-12">{{
              t('CRM.FORM.LABEL_STATUS')
            }}</label>
            <select
              v-model="status"
              class="border border-n-weak rounded px-2 py-1.5 text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500"
            >
              <option
                v-for="opt in statusOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-12">{{
              t('CRM.FORM.LABEL_ORIGIN')
            }}</label>
            <select
              v-model="crmOriginId"
              class="border border-n-weak rounded px-2 py-1.5 text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500"
            >
              <option :value="null">{{ t('CRM.FORM.NO_ORIGIN') }}</option>
              <option
                v-for="opt in originOptions"
                :key="opt.id"
                :value="opt.id"
              >
                {{ opt.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Row 7: Data de fechamento + Próximo contato -->
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-12">{{
              t('CRM.FORM.LABEL_CLOSE_DATE')
            }}</label>
            <input
              v-model="closeDate"
              type="date"
              class="border border-n-weak rounded px-2 py-1.5 text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-12">{{
              t('CRM.FORM.LABEL_NEXT_CONTACT')
            }}</label>
            <input
              v-model="nextContactAt"
              type="datetime-local"
              class="border border-n-weak rounded px-2 py-1.5 text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />
          </div>
        </div>

        <!-- Row 7b: Motivo da perda (apenas quando status = perdida) -->
        <div v-if="status === 'lost'" class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-12">{{
            t('CRM.FORM.LABEL_LOST_REASON')
          }}</label>
          <input
            v-model="lostReason"
            type="text"
            :placeholder="t('CRM.FORM.PLACEHOLDER_LOST_REASON')"
            class="border rounded px-2 py-1.5 text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500"
            :class="lostReasonError ? 'border-n-ruby-9' : 'border-n-weak'"
          />
          <p v-if="lostReasonError" class="text-xs text-n-ruby-9">
            {{ t('CRM.FORM.LOST_REASON_REQUIRED') }}
          </p>
        </div>

        <!-- Row 8: Notas -->
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-12">{{
            t('CRM.FORM.LABEL_NOTES')
          }}</label>
          <textarea
            v-model="notes"
            rows="3"
            :placeholder="t('CRM.FORM.PLACEHOLDER_NOTES')"
            class="border border-n-weak rounded px-2 py-1.5 text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500 resize-none"
          />
        </div>
      </template>
    </div>
  </Dialog>
</template>
